import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ThumbsUp, MessageCircle, Share2, UserPlus, UserCheck,
  Volume2, VolumeX, X, Play, Sparkles, Film, Crown, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { hideOnError } from "../lib/imgFallback";
import { displayName } from "../lib/displayName";
import "./Clips.css";

/**
 * TikTok-style vertical full-screen clips player.
 * – Snap-scroll per slide
 * – Autoplay on view, pause when out of view (IntersectionObserver)
 * – Tap video to play/pause; mute toggle in top bar
 * – Right-side overlay: like / comment / share / follow
 * – Bottom overlay: author + caption
 */
export default function Clips() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [commentsOpen, setCommentsOpen] = useState(null); // postId or null
  const trackRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clips", { params: { limit: 60 } });
      setClips(data);
    } catch (e) {
      toast.error("Failed to load clips");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); document.body.classList.add("clips-open"); return () => document.body.classList.remove("clips-open"); }, [load]);

  // Track active slide via IntersectionObserver
  useEffect(() => {
    if (!trackRef.current || clips.length === 0) return;
    const slides = trackRef.current.querySelectorAll(".clip-slide");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const idx = parseInt(entry.target.dataset.idx, 10);
        const video = entry.target.querySelector("video");
        if (!video) return;
        if (entry.intersectionRatio >= 0.6) {
          setActiveIdx(idx);
          video.muted = muted;
          video.currentTime = 0;
          video.play().catch(() => {});
          // Fire view tracking (deduped server-side)
          const post = clips[idx];
          if (post && user && !post.id?.startsWith("ad_")) {
            api.post(`/posts/${post.id}/view`).catch(() => {});
          }
        } else {
          video.pause();
        }
      });
    }, { root: trackRef.current, threshold: [0, 0.6, 0.9] });
    slides.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [clips, muted, user]);

  // Apply mute change to all videos
  useEffect(() => {
    if (!trackRef.current) return;
    trackRef.current.querySelectorAll("video").forEach((v) => { v.muted = muted; });
  }, [muted]);

  const handleVideoTap = (e) => {
    const v = e.currentTarget;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const updateClip = (id, patch) => {
    setClips((prev) => prev.map((c) => c.id === id ? { ...c, ...patch } : c));
  };

  if (loading) {
    return (
      <div className="clips-player" data-testid="clips-loading">
        <div className="clip-topbar">
          <Link to="/feed" className="clip-topbar-btn" data-testid="clips-close-btn"><X size={20} strokeWidth={1.5} /></Link>
          <span className="clip-topbar-title">Clips</span>
          <div style={{ width: 40 }} />
        </div>
        <div className="clip-empty" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="font-serif text-2xl text-white/70">Loading clips…</div>
        </div>
      </div>
    );
  }

  if (clips.length === 0) {
    return (
      <div className="clips-player" data-testid="clips-empty-player">
        <div className="clip-topbar">
          <Link to="/feed" className="clip-topbar-btn" data-testid="clips-close-btn"><X size={20} strokeWidth={1.5} /></Link>
          <span className="clip-topbar-title">Clips</span>
          <div style={{ width: 40 }} />
        </div>
        <div className="clip-empty" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
          <Film size={56} strokeWidth={1} className="text-white/40" />
          <div className="font-serif text-3xl text-white/80">No clips yet</div>
          <p className="text-sm text-white/60 max-w-xs text-center">Be the first to share a moment from the cauldron.</p>
          <Link to="/clips/new" className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-6 py-2.5 rounded-sm uppercase tracking-wider text-xs" data-testid="clips-empty-post-link">
            <Sparkles size={12} strokeWidth={1.5} /> Post a clip
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="clips-player" data-testid="clips-player">
      {/* Top bar */}
      <div className="clip-topbar">
        <Link to="/feed" className="clip-topbar-btn" data-testid="clips-close-btn" aria-label="Close">
          <X size={20} strokeWidth={1.5} />
        </Link>
        <span className="clip-topbar-title">Clips</span>
        <div style={{ display: "flex", gap: 6 }}>
          <Link to="/clips/new" className="clip-topbar-btn" data-testid="clips-post-btn" aria-label="Post a clip">
            <Plus size={18} strokeWidth={2} />
          </Link>
          <button onClick={() => setMuted((m) => !m)} className="clip-topbar-btn" data-testid="clips-mute-btn" aria-label={muted ? "Unmute" : "Mute"}>
            {muted ? <VolumeX size={18} strokeWidth={1.5} /> : <Volume2 size={18} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <div className="clips-track" ref={trackRef} data-testid="clips-track">
        {clips.map((clip, idx) => (
          <Slide
            key={clip.id}
            clip={clip}
            idx={idx}
            isActive={idx === activeIdx}
            muted={muted}
            currentUser={user}
            onVideoTap={handleVideoTap}
            onUpdate={(patch) => updateClip(clip.id, patch)}
            onOpenComments={() => setCommentsOpen(clip.id)}
            nav={nav}
          />
        ))}
      </div>

      {commentsOpen && (
        <CommentsSheet
          postId={commentsOpen}
          onClose={() => setCommentsOpen(null)}
          onPosted={() => updateClip(commentsOpen, { commentCount: ((clips.find(c => c.id === commentsOpen)?.commentCount) || 0) + 1 })}
        />
      )}
    </div>
  );
}

function Slide({ clip, idx, isActive, muted, currentUser, onVideoTap, onUpdate, onOpenComments, nav }) {
  const videoRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  const liked = currentUser && clip.likes?.includes(currentUser.id);
  const likeCount = clip.likes?.length || 0;
  const isOwner = currentUser && currentUser.id === clip.userId;
  const authorName = displayName({ ...(clip.provider || {}), user: clip.user }) || "Anon";
  const authorAvatar = clip.provider?.avatarUrl || clip.user?.avatarUrl;

  // Check follow (friend) status once
  useEffect(() => {
    if (!currentUser || isOwner) return;
    let cancelled = false;
    api.get(`/friends/status/${clip.userId}`)
      .then(({ data }) => {
        if (cancelled) return;
        // "friends" = accepted | "pending" (outgoing) = request sent | "none" = no relation
        setFollowing(data.status === "friends" || (data.status === "pending" && data.direction === "outgoing"));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser?.id, clip.userId, isOwner]);

  const onTime = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleLike = async () => {
    if (!currentUser) return toast.error("Sign in to react");
    try {
      const { data } = await api.post(`/posts/${clip.id}/like`);
      onUpdate({ likes: data.likes, dislikes: data.dislikes, reactions: data.reactions });
    } catch { /* noop */ }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/feed?post=${clip.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: `${authorName} on Cauldron`, text: clip.content || "Watch this clip", url }); return; } catch { /* fallthrough */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Could not share"); }
  };

  const handleFollow = async () => {
    if (!currentUser || isOwner) return;
    setFollowBusy(true);
    try {
      if (following) {
        // Try un-friend first (accepted), fall back to cancel-pending
        try { await api.delete(`/friends/${clip.userId}`); }
        catch { await api.post(`/friends/cancel/${clip.userId}`); }
        setFollowing(false);
        toast.success("Unfollowed");
      } else {
        await api.post("/friends/request", { toUserId: clip.userId });
        setFollowing(true);
        toast.success("Friend request sent");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setFollowBusy(false); }
  };

  const onPlayHandler = () => setPaused(false);
  const onPauseHandler = () => setPaused(true);

  return (
    <div className="clip-slide" data-idx={idx} data-testid={`clip-slide-${idx}`}>
      <video
        ref={videoRef}
        src={clip.mediaUrl}
        playsInline
        loop
        muted={muted}
        preload={isActive ? "auto" : "metadata"}
        onClick={onVideoTap}
        onTimeUpdate={onTime}
        onPlay={onPlayHandler}
        onPause={onPauseHandler}
        data-testid={`clip-video-${idx}`}
      />

      {paused && isActive && (
        <div className="clip-paused-indicator" data-testid={`clip-paused-${idx}`}>
          <div><Play size={36} strokeWidth={1.5} fill="currentColor" /></div>
        </div>
      )}

      {/* Right-rail action stack */}
      <div className="clip-actions">
        <Link to={`/profile/${clip.userId}`} className="clip-author-avatar" style={{ marginBottom: -8 }} data-testid={`clip-author-link-${idx}`}>
          {authorAvatar ? (
            <img src={authorAvatar} alt="" onError={hideOnError} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : authorName?.[0]?.toUpperCase()}
        </Link>
        {currentUser && !isOwner && (
          <button onClick={handleFollow} disabled={followBusy}
            className="clip-action-btn"
            style={{ marginTop: -28, background: following ? "rgba(197,160,89,0.85)" : "rgba(217,92,20,0.95)", border: "none", width: 24, height: 24 }}
            data-testid={`clip-follow-${idx}`}
            aria-label={following ? "Unfollow" : "Follow"}
          >
            {following ? <UserCheck size={14} strokeWidth={2} /> : <UserPlus size={14} strokeWidth={2} />}
          </button>
        )}

        <button onClick={handleLike} className="clip-action-btn" data-testid={`clip-like-${idx}`} aria-label="Like">
          <ThumbsUp size={22} strokeWidth={liked ? 2 : 1.5} fill={liked ? "currentColor" : "none"} color={liked ? "#D95C14" : "#fff"} />
        </button>
        <span className="clip-action-label" data-testid={`clip-likes-${idx}`}>{likeCount}</span>

        <button onClick={onOpenComments} className="clip-action-btn" data-testid={`clip-comments-${idx}`} aria-label="Comments">
          <MessageCircle size={22} strokeWidth={1.5} />
        </button>
        <span className="clip-action-label">{clip.commentCount || 0}</span>

        <button onClick={handleShare} className="clip-action-btn" data-testid={`clip-share-${idx}`} aria-label="Share">
          <Share2 size={22} strokeWidth={1.5} />
        </button>
      </div>

      {/* Bottom meta */}
      <div className="clip-meta">
        <div className="clip-author-row">
          <Link to={`/profile/${clip.userId}`} className="clip-author-name" style={{ color: "#fff", textDecoration: "none" }} data-testid={`clip-author-name-${idx}`}>
            @{authorName}
          </Link>
          {clip.provider?.verified && <Sparkles size={14} className="text-secondary" />}
          {clip.provider?.elder && (
            <span title={clip.provider.elderIndex ? `Elder · #${clip.provider.elderIndex} of 500` : "Elder Practitioner"} style={{ display: "inline-flex" }} data-testid={`clip-elder-${idx}`}>
              <Crown size={14} className="text-primary" strokeWidth={2} />
            </span>
          )}
        </div>
        {clip.content && <div className="clip-caption" data-testid={`clip-caption-${idx}`}>{clip.content}</div>}
      </div>

      <div className="clip-progress">
        <div className="clip-progress-fill" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

function CommentsSheet({ postId, onClose, onPosted }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/posts/${postId}/comments`).then(({ data }) => setComments(data)).catch(() => {});
  }, [postId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${postId}/comments`, { content: text });
      setComments((c) => [...c, data]);
      setText("");
      onPosted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 60, display: "flex", alignItems: "flex-end" }}
      data-testid="clip-comments-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", background: "#15102A", borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "70vh", display: "flex", flexDirection: "column", padding: "16px 16px 12px" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h3 className="font-serif text-xl text-white">Comments · {comments.length}</h3>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="clip-comments-close"><X size={20} strokeWidth={1.5} /></button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
          {comments.length === 0 ? (
            <div className="text-white/50 italic font-serif text-base py-6 text-center">Be the first to react.</div>
          ) : comments.map((c) => (
            <div key={c.id} className="text-sm text-white/85 mb-3" data-testid={`clip-comment-${c.id}`}>
              <span className="font-serif text-secondary mr-2">{displayName({ userName: c.userName, name: c.userName })}</span>
              {c.content}
            </div>
          ))}
        </div>
        <form onSubmit={submit} style={{ display: "flex", gap: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…"
            className="flex-1 bg-input/40 border border-secondary/20 rounded-sm px-3 py-2 text-sm text-white" data-testid="clip-comment-input" />
          <button disabled={busy} className="bg-primary text-primary-foreground px-4 py-2 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50" data-testid="clip-comment-submit">Post</button>
        </form>
      </div>
    </div>
  );
}
